import { useCallback, useMemo } from 'react'

import { type Edge as ReactFlowEdge } from '@xyflow/react'

import { buildChildrenMap, getDescendants } from './treeUtils'
import { type CustomNode } from './types'

interface UseMultiSelectProps {
  nodes: CustomNode[]
  edges: ReactFlowEdge[]
  setNodes: React.Dispatch<React.SetStateAction<CustomNode[]>>
}

const useMultiSelect = ({ nodes, edges, setNodes }: UseMultiSelectProps) => {
  const checkedNodes = useMemo(() => nodes.filter((n) => n.data.checked), [nodes])

  const onUnselectAll = useCallback(() => {
    setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, checked: false } })))
  }, [setNodes])

  const onMultiSelectNode = useCallback(
    (nodeId: string, isChecked: boolean) => {
      const childrenMap = buildChildrenMap(edges)
      const descendants = getDescendants(nodeId, childrenMap)
      const nodeIdsToUpdate = new Set([nodeId, ...descendants])
      // Nodes that have children need to be expanded when checked
      const nodesWithChildren = new Set([
        nodeId,
        ...descendants.filter((id) => childrenMap.has(id)),
      ])

      setNodes((nds) =>
        nds.map((n) => {
          if (nodeIdsToUpdate.has(n.id)) {
            const hasChildren = nodesWithChildren.has(n.id)
            return {
              ...n,
              selectable: false,
              selected: false,
              data: {
                ...n.data,
                checked: isChecked,
                expanded: hasChildren ? isChecked : n.data.expanded,
              },
            }
          }
          return n
        })
      )
    },
    [edges, setNodes]
  )

  return { checkedNodes, onMultiSelectNode, onUnselectAll }
}

export default useMultiSelect
